import React, { useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconArrowLeft } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  getQuizDetailsOptions,
  getQuizzesOptions,
  Plan,
  Quiz,
} from '@cfa/pathway-fe-shared/xp';
import { Button, LoadingIndicator } from '@cfa/react-core';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { itemByLanguage } from '@/utils/language';
import { isApiError } from '@/utils/request';

import QuizPreviewCard from '@/components/Cards/QuizPreviewCard';
import GenericError from '@/components/Error/GenericError';
import NoMessage from '@/components/NoMessage/NoMessage';
import { PathwayModal } from '@/components/popups/PathwayModal';

import Constants from '@/constants';

import QuizLibraryCard from '../Cards/QuizLibraryCard';

import { sourceDataFamily, useDataView } from '@/atoms/data-view';

interface QuizLibraryPopUpProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Plan;
  sectionId: string;
}

const sourceData = sourceDataFamily('build-quizzes');

const QuizLibraryPopUp: React.FC<QuizLibraryPopUpProps> = ({
  isOpen,
  onClose,
  plan,
  sectionId,
}) => {
  const { t } = useTranslation();
  const [quizForPreview, setQuizForPreview] = useState<Quiz>();
  const { addSteps, isPending: isAddingQuiz } = useUpdatePlan({
    onSuccess: () => {
      onClose();
    },
  });
  const [
    {
      display: { items },
    },
  ] = useDataView<Quiz>('build-quizzes');

  const {
    data: quizzes,
    error: errorGettingQuizzes,
    isFetching: isFetchingQuizzes,
  } = useQuery({
    ...getQuizzesOptions(),
    enabled: isOpen && !quizForPreview,
  });

  const {
    data: quizDetails,
    error: quizDetailsError,
    isFetching: isFetchingQuizDetails,
  } = useQuery({
    ...getQuizDetailsOptions({
      path: { quizId: quizForPreview?.id ?? '' },
    }),
    enabled: !!quizForPreview?.id,
  });

  const setSourceData = useSetAtom(sourceData);

  useEffect(() => {
    setSourceData(quizzes ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizzes]);

  useEffect(() => {
    if (!isOpen) {
      setQuizForPreview(undefined);
    }
  }, [isOpen]);

  if (!quizForPreview && isApiError(errorGettingQuizzes)) {
    datadogRum.addError(errorGettingQuizzes);
    return <GenericError />;
  }

  const withinSection = !!sectionId.length;

  const getQuizStep = (quiz: Quiz) => ({
    id: uuidv4(),
    name: {
      en: quiz.name.en ?? quiz.name.es ?? '',
      es: quiz.name.es ?? quiz.name.en ?? '',
    },
    type: Constants.STEP_TYPES.QUIZ,
    quizId: quiz.typeFormId,
    reference: undefined,
    note: undefined,
    urls: [],
    available: true,
  });

  const onAddQuiz = async (quiz: Quiz) => {
    if (!plan) return;
    if (!quiz.typeFormId) {
      datadogRum.addError(new Error('Quiz missing typeFormId'), {
        quizId: quiz.id,
      });
      return;
    }
    const quizStep = getQuizStep(quiz);
    await addSteps(plan, [quizStep], withinSection ? sectionId : undefined);
  };

  const onAddQuizFromPreview = () => {
    if (!quizForPreview || isAddingQuiz) return;
    void onAddQuiz(quizForPreview);
  };

  const isPreview = !!quizForPreview;
  const quizName = quizForPreview?.name
    ? itemByLanguage(quizForPreview.name)
    : '';

  const isLoading = isPreview ? isFetchingQuizDetails : isFetchingQuizzes;

  return (
    <>
      <PathwayModal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        stepKey={isPreview ? 'preview-quiz' : 'list-quizzes'}
      >
        <PathwayModal.Header>
          {isPreview ? (
            <div className="flex w-full items-center">
              <div className="flex flex-1 whitespace-nowrap">
                <Button
                  color="secondary"
                  onPress={() => setQuizForPreview(undefined)}
                  size="sm"
                  variant="text"
                  className="-translate-x-2.5! pl-0!"
                >
                  <IconArrowLeft className="pr-1!" />
                </Button>
              </div>
              <div className="text-center">
                <p className="mb-2 text-xs font-medium tracking-widest uppercase">
                  {t('TrainingPlans.preview')}
                </p>
                {quizName}
              </div>
              <div className="min-w-[155px] flex-1" />
            </div>
          ) : (
            <PathwayModal.Title>
              {t('TrainingPlans.addQuiz.title')}
            </PathwayModal.Title>
          )}
        </PathwayModal.Header>
        <PathwayModal.Body noMinHeight>
          {isLoading ? (
            <div className="flex h-full items-center justify-center py-16">
              <LoadingIndicator size="lg" />
            </div>
          ) : isPreview ? (
            <QuizPreviewCard quiz={quizDetails} quizError={quizDetailsError} />
          ) : (
            <>
              {items.length ? (
                <>
                  {items.map(quiz => (
                    <QuizLibraryCard
                      key={quiz.id}
                      onAddQuiz={onAddQuiz}
                      onPreviewQuiz={setQuizForPreview}
                      quiz={quiz}
                      quizAddButtonTestId="QuizLibraryAddButton"
                      quizNameTestId="QuizLibraryName"
                    />
                  ))}
                </>
              ) : (
                <NoMessage
                  message={t('TrainingPlans.buildQuizzes.noQuizzesPopup')}
                />
              )}
            </>
          )}
        </PathwayModal.Body>
        {isPreview && (
          <PathwayModal.Footer>
            <PathwayModal.PrimaryButton
              isDisabled={isAddingQuiz}
              isPending={isAddingQuiz}
              onPress={onAddQuizFromPreview}
              testId="addQuizFromPreviewButton"
            >
              {t('Button.add')}
            </PathwayModal.PrimaryButton>
          </PathwayModal.Footer>
        )}
      </PathwayModal>
    </>
  );
};

export default QuizLibraryPopUp;
